import { useEffect, useRef, useState } from "react";
import { Check, Loader2, Palette } from "lucide-react";
import api from "@/lib/api";

const Chip = ({ o, active, busy, onPick, testid }) => (
  <button type="button" disabled={busy} onClick={() => onPick(o.colors, o.name)} title={`${o.colors.join(" ")}${o.meaning ? " — " + o.meaning : ""}`}
    className={`flex items-center gap-1.5 rounded-full border px-2 py-1 text-[11px] transition-colors ${active ? "border-primary bg-primary/10 font-bold" : "border-border hover:border-primary/40"}`} data-testid={testid}>
    <span className="flex -space-x-1 rtl:space-x-reverse">{o.colors.slice(0, 4).map((hx, j) => <span key={hx + j} className="inline-block w-4 h-4 rounded-full border border-white/70 shadow-sm" style={{ background: hx }} />)}</span>
    {active && <Check className="w-3 h-3 text-primary" />}{o.name}
  </button>
);

/** خيارات ألوان مبنية على نظرية الألوان (أحادية/متجاورة/مكمّلة…) + لوحات جاهزة، مع دلالة كل لوحة — يجرّبها العميل على المعاينة قبل التوليد الحقيقي */
export const PaletteVariants = ({ base = [], selected = [], onPick, busy = false, testid = "palette-variants" }) => {
  const [variants, setVariants] = useState([]);
  const [phase, setPhase] = useState("");  // "" | applying | done — تأكيد مرئي أن اللون يتغيّر ثم تم
  const doneTimer = useRef(null);
  const key = (base || []).join(",");
  useEffect(() => {
    if (!base?.length) { setVariants([]); return; }
    api.post("/content/palette-variants", { palette: base }).then((r) => setVariants(r.data.variants || [])).catch(() => setVariants([]));
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {  // عند انتهاء التطبيق (انتهى busy أو لا معاينة غير متزامنة) → «تم» لثوانٍ
    if (phase !== "applying" || busy) return;
    const t = setTimeout(() => { setPhase("done"); doneTimer.current = setTimeout(() => setPhase(""), 4000); }, busy ? 0 : 700);
    return () => clearTimeout(t);
  }, [phase, busy]);
  useEffect(() => () => clearTimeout(doneTimer.current), []);
  if (!base?.length) return null;
  const same = (a, b) => (a || []).join(",").toUpperCase() === (b || []).join(",").toUpperCase();
  const cur = selected?.length ? selected : base;
  const derived = [{ name: "ألوانك الأصلية", colors: base }, ...variants.filter((v) => v.group !== "ready")];
  const ready = variants.filter((v) => v.group === "ready");
  const activeOpt = [...derived, ...ready].find((o) => same(o.colors, cur));
  const pick = (cols, name) => { setPhase("applying"); clearTimeout(doneTimer.current); onPick(cols, name); };
  const working = busy || phase === "applying";
  return (
    <div className="rounded-lg border border-border bg-background/70 p-2 space-y-2" data-testid={testid}>
      <p className="text-[11px] text-muted-foreground flex items-center gap-1 flex-wrap"><Palette className="w-3.5 h-3.5 text-primary" /> اختر لوحة الألوان بأريحية قبل التوليد الحقيقي — المعاينة تتلوّن فورًا، والنتيجة النهائية تلتزم بها حرفيًا.</p>
      {working && (
        <div className="flex items-center gap-2 rounded-md bg-primary/10 border border-primary/30 px-2 py-1.5 text-xs font-bold text-primary" role="status" data-testid={`${testid}-busy`}>
          <Loader2 className="w-4 h-4 animate-spin" /> جاري تغيير اللون حسب اختيارك… لحظات
        </div>
      )}
      {!working && phase === "done" && (
        <div className="flex items-center gap-2 rounded-md bg-emerald-500/10 border border-emerald-500/30 px-2 py-1.5 text-xs font-bold text-emerald-700 dark:text-emerald-400" role="status" data-testid={`${testid}-done`}>
          <Check className="w-4 h-4" /> تم تطبيق اللوحة «{activeOpt?.name || "المختارة"}» — المعاينة والتوليد النهائي يلتزمان بها
        </div>
      )}
      <div>
        <div className="text-[10px] font-bold text-muted-foreground mb-1">مشتقة من ألوانك (أحادية · متجاورة · مكمّلة)</div>
        <div className="flex flex-wrap gap-1.5">{derived.map((o, i) => <Chip key={"d" + i} o={o} active={same(o.colors, cur)} busy={working} onPick={pick} testid={`${testid}-${i}`} />)}</div>
      </div>
      {ready.length > 0 && (
        <div>
          <div className="text-[10px] font-bold text-muted-foreground mb-1">لوحات احترافية جاهزة</div>
          <div className="flex flex-wrap gap-1.5">{ready.map((o, i) => <Chip key={"r" + i} o={o} active={same(o.colors, cur)} busy={working} onPick={pick} testid={`${testid}-ready-${i}`} />)}</div>
        </div>
      )}
      {activeOpt?.meaning && <p className="text-[11px] text-primary" data-testid={`${testid}-meaning`}>دلالة اللوحة: {activeOpt.meaning}</p>}
    </div>
  );
};
